import type { AuthResponse } from '@batixa/domain';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

let accessToken: string | null = null;
let refreshing: Promise<AuthResponse | null> | null = null;
let onSessionLost: (() => void) | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}
export function onUnauthorized(handler: () => void) {
  onSessionLost = handler;
}

async function parse(res: Response) {
  if (res.status === 204) return undefined;
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const err = data?.error ?? {};
    throw new ApiError(res.status, err.code ?? 'HTTP_' + res.status, err.message ?? 'Erreur réseau', err.details);
  }
  return data;
}

/** Renouvelle le jeton d'accès via le cookie httpOnly (une seule requête à la fois). */
export function refreshSession(): Promise<AuthResponse | null> {
  refreshing ??= fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' })
    .then(async (res) => (res.ok ? ((await res.json()) as AuthResponse) : null))
    .catch(() => null)
    .then((session) => {
      setAccessToken(session?.accessToken ?? null);
      return session;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  auth?: boolean;
}

/** Télécharge un fichier protégé (même renouvellement de session que les appels JSON). */
export async function apiFile(path: string): Promise<{ blob: Blob; filename: string }> {
  const send = () => fetch('/api' + path, { credentials: 'include', headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {} });
  let res = await send();
  if (res.status === 401 && (await refreshSession())) res = await send();
  if (!res.ok) await parse(res);
  const disposition = res.headers.get('content-disposition') ?? '';
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'document';
  return { blob: await res.blob(), filename };
}

export async function api<T>(path: string, { method = 'GET', body, auth = true }: RequestOptions = {}): Promise<T> {
  const send = () =>
    fetch('/api' + path, {
      method,
      credentials: 'include',
      headers: {
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(auth && accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  let res: Response;
  try {
    res = await send();
  } catch {
    throw new ApiError(0, 'NETWORK', 'Connexion au serveur impossible. Vérifiez votre réseau.');
  }
  if (res.status === 401 && auth) {
    const session = await refreshSession();
    if (!session) {
      onSessionLost?.();
      return parse(res);
    }
    res = await send();
  }
  return parse(res);
}
